import localFont from "@next/font/local";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
const { BLOG_URL, CONTENT_API_KEY } = process.env;
import { RiTimerFlashLine } from "react-icons/ri";
import CtaOne from "@components/CTA/CtaOne";
import BlogCard from "@components/Blog/BlogCard";
import Rating from "@components/Blog/Rating";
import { ButtonPrimary, ButtonSecondary } from "@components/Buttons";
import { createUTMQueryParams } from "@utils/signature_owner";
import BlogCard2 from "@components/Blog/BlogCard2";
import {
  CLMButtonPrimary,
  CLMButtonSecondaryBorder,
} from "components/CLM/v3/Buttons";
import { featuredBlogs } from "@constants/blogs";

const getPosts = async (page = 1) => {
  // curl ""
  const res = await fetch(
    `${BLOG_URL}/ghost/api/v3/content/posts/?key=${CONTENT_API_KEY}&include=tags&filter=primary_tag:clm&limit=9&page=${page}`
  ).then((res) => res.json());
  const posts = res.posts;
  let newPosts = [];
  let pagination = res.meta.pagination;
  // console.log(posts.length)
  posts.forEach((post) => {
    // console.log("========", post?.primary_tag?.slug?.toString());
    if (post?.primary_tag?.slug?.toString() != "boloforms") newPosts.push(post);
  });
  // console.log(newPosts[0]);
  return { newPosts, pagination };
};

// Ghost CMS Request
export const getStaticProps = async ({ params }) => {
  try {
    const { page } = params;

    const isNumeric = (str) => {
      if (typeof str != "string") return false;
      return !isNaN(str) && !isNaN(parseFloat(str));
    };
    // if page is not number then return not found
    if (!isNumeric(page.toString()))
      return {
        notFound: true,
      };

    const { newPosts, pagination } = await getPosts(page);
    if (pagination.page > pagination.pages) {
      return {
        notFound: true,
      };
    }
    return {
      props: { posts: newPosts, pagination },
      revalidate: 24 * 60 * 60,
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

// Static Render all the pages
export const getStaticPaths = async () => {
  const { pagination } = await getPosts();
  let paths = new Array(pagination?.pages || 0)
    .fill(null)
    .map((_, page) => ({ params: { page: (page + 1).toString() } }));

  return {
    paths: paths,
    fallback: true,
  };
};

const cardsColor = {
  signature: "bg-green-100",
  contracts: "bg-red-100",
};

const biotif = localFont({
  src: [
    {
      path: "../../../assets/fonts/biotif-regular.ttf",
      weight: "400",
      style: "normal",
    },
    {
      path: "../../../assets/fonts/biotif-bold.ttf",
      weight: "700",
      style: "bold",
    },
    {
      path: "../../../assets/fonts/biotif-black.ttf",
      weight: "900",
      style: "black",
    },
    {
      path: "../../../assets/fonts/biotif-light.ttf",
      weight: "300",
      style: "light",
    },
  ],
});

function Blogs(props) {
  const { posts, pagination } = props;
  // console.log("POSTS 2", posts);
  const router = useRouter();
  if (!posts) {
    return (
      <div className="flex justify-center items-center">
        <h3 className="text-2xl font-bold">No Posts Found</h3>
      </div>
    );
  }
  const handlePush = (link) => {
    router.push(link);
  };

  const schema1 = {
    "@context": "https://schema.org/",
    "@type": "WebSite",
    name: "Latest Blogs Of Boloforms",
    url: "https://www.boloforms.com/blog/",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.boloforms.com/blog/{search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const schema2 = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.boloforms.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Latest Blogs",
        item: "https://www.boloforms.com/blog/",
      },
    ],
  };

  const schema3 = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Boloforms",
    alternateName: "Workflow Automated Software",
    url: "https://www.boloforms.com/blog/",
    logo: "https://www.boloforms.com/_next/static/media/logo-text.e6f7617c.svg",
    sameAs: [
      "https://www.facebook.com/boloforms/",
      "https://twitter.com/BoloForms",
      "https://www.youtube.com/channel/UCjbA2sUd7jxdtiuwadt7VKg",
      "https://www.boloforms.com/blog/",
    ],
  };

  return (
    <div className="w-full md:mt-20 mt-8">
      <Head>
        <title>BoloSign - AI for Smart Contract Management | Blogs</title>
        <meta
          name="description"
          content="AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies."
        />
        <link
          rel="icon"
          href="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/Logo.png"
        />

        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="BoloSign" />
        <meta name="publisher" content="BoloSign" />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />

        {/* <!-- Facebook Meta Tags --> */}
        <meta
          property="og:url"
          content={`https://bolosign.com/blog/page/${pagination.page}`}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="BoloSign - AI for Smart Contract Management | Blogs"
        />
        <meta
          property="og:description"
          content="AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies."
        />
        <meta
          property="og:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        {/* <!-- Twitter Meta Tags --> */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="https://bolosign.com" />
        <meta
          property="twitter:url"
          content={`https://bolosign.com/blog/page/${pagination.page}`}
        />
        <meta
          name="twitter:title"
          content="BoloSign - AI for Smart Contract Management | Blogs"
        />
        <meta
          name="twitter:description"
          content="AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies."
        />
        <meta
          name="twitter:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        <link
          rel="canonical"
          href={`https://www.bolosign.com/blog/page/${pagination.page}`}
        />
      </Head>

      <div className="max-w-screen-xl mx-auto md:px-8 px-4">
        {/* top section  */}
        <div className="w-full max-w-screen-mxl mx-auto px-4 md:px-8 xxl:px-0 flex flex-col md:flex-row justify-between items-center md:gap-6 lg:gap-20 mxl:gap-40">
          <div className="flex-1">
            <CtaOne
              heading2={`Take a Look at Our Featured Articles`}
              paragraph={`These software solutions provide centralized platform planning and monitoring task lifecycle.`}
            />
          </div>
          <div className="flex-1 flex flex-col w-full gap-10 md:gap-8">
            <div className="flex gap-2 xs:gap-4">
              <CLMButtonPrimary
                buttonText="Get Started"
                buttonUrl={`https://signature.boloforms.com/register?from=WEBSITE_BLOG_PAGE_HERO${createUTMQueryParams(
                  {
                    from: "WEBSITE_BLOG_PAGE_HERO",
                  }
                )}`}
              />
              <CLMButtonSecondaryBorder
                buttonText="Buy Now"
                buttonUrl={`/signature/pricing`}
              />
            </div>
            <div className="">
              <Rating rate={4.7} />
            </div>
          </div>
        </div>
        {/* featured blogs */}
        <div className="w-full py-20 flex flex-col md:flex-row gap-6 sm:max-w-[80%] md:max-w-screen-mxl mx-auto">
          {featuredBlogs.map((card, index) => (
            <BlogCard key={index} {...card} />
          ))}
        </div>
        {/* blogs */}
        <div className="w-full max-w-screen-mxl mx-auto py-16  flex flex-col gap-12">
          <div className="">
            <CtaOne heading2={`See Latest Articles`} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 w-full sm:max-w-[80%] md:max-w-full mx-auto">
            {posts.map((post, index) => (
              <BlogCard2
                key={index}
                blogImage={post.feature_image}
                title={post.title}
                genre={
                  post?.primary_tag?.name.charAt(0).toUpperCase() +
                    post?.primary_tag?.name.slice(1) || "BoloForms"
                }
                date={new Date(post.published_at).toShortFormat()}
                blogUrl={`/blog/${post.slug}`}
                customTitle={`text-xl`}
                imageBorders={false}
                genreColor={cardsColor[post?.primary_tag?.name] ?? "bg-red-100"}
                meta_description={post.meta_description}
              />
            ))}
          </div>
        </div>
      </div>
      {/* <h2 className="text-lg sm:text-2xl font-semibold mb-4">Blogs</h2>
      <ul>
        {posts.map((post, index) => {
          return (
            <li
              style={{ listStyle: "none" }}
              key={post.slug}
              className="bg-gray-100 rounded-xl mt-[24px]  shadow-sm border"
            >
              <Link href="/blog/[slug]" as={`/blog/${post.slug}`}>
                <div
                  style={{
                    background:
                      "radial-gradient(60.77% 60.77% at 50% 50%, rgba(255, 255, 255, 0) 0%, rgba(233, 216, 253, 0.2) 100%)",
                  }}
                  className="flex w-full flex-col md:flex-row sm:flex-row"
                >
                  <div className="flex w-full md:w-9/12 lg:w-9/12  py-[24px] pl-[24px] pr-[24px] md:pr-0 lg:pr-0 flex-col">
                    <div className="flex w-full flex-row flex-wrap gap-[10px] md:gap-0 lg:gap-0">
                      <div className="px-[8px] py-[4px] text-[12px] leading-[1] text-[#44337A] font-semibold bg-[#E9D8FD] rounded-[5px]">
                        {post?.primary_tag?.name?.toUpperCase() || "BoloForms"}
                      </div>
                      <div className="ml-[24px] text-[12px] leading-[1.5] py-[1px]">
                        {new Date(post.published_at).toShortFormat()}
                      </div>
                      <div className="flex grow" />
                      <div
                        className={`${biotif.className} flex text-[12px] leading[1.5] items-center justify-center`}
                      >
                        <RiTimerFlashLine size="18px" className="mr-[4px]" />
                        {post.reading_time} min read
                      </div>
                    </div>
                    <div className="flex w-full flex-row">
                      <div
                        className={`${biotif.className} flex text-[32px] mt-[16px] text-ellipsis leading-[1] min-h-[32px] font-bold items-start justify-center`}
                      >
                        {post.title}
                      </div>
                    </div>
                    <div className="flex w-full flex-row mt-[16px]">
                      <div
                        className={`${biotif.className} flex text-[16px] text-ellipsis leading-[1] min-h-[48px] font-regular items-start justify-center`}
                      >
                        {post.excerpt.slice(0, 250)}
                        {post.excerpt.length > 250 ? "..." : ""}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      clipPath: "polygon(15% 0%, 100% 0%,100% 100%,0% 100%)",
                      backgroundSize: "cover",
                      backgroundImage: `linear-gradient(99.4deg, rgba(255, 255, 255, 0.64),rgba(255, 255, 255, 0.04)),url(${
                        post.feature_image ??
                        "https://blog.boloforms.com/content/images/2023/01/large-promo-sheetgod--1400---560-px---1--14.png"
                      })`,
                    }}
                    className="flex w-3/12 rounded-r-xl"
                  ></div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul> */}
      {/* <div className="max-w-screen-mxl mx-auto flex justify-center items-center gap-1 mb-20">
        <button
          aria-label="Previous"
          onClick={() => {
            handlePush(
              pagination?.prev
                ? `/blog/page/${pagination?.prev}`
                : `/blog/page/$${pagination?.page}/#`
            );
          }}
          disabled={!pagination?.prev}
          className={`${
            pagination?.prev
              ? "hover:bg-[#a97dff] hover:text-white"
              : "opacity-50 cursor-not-allowed"
          } w-fit my-3 flex justify-center items-center text-lg text-center font-semibold px-1 py-1 border-2 border-[#915bff] rounded-lg text-[#915bff] transition-colors`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 rotate-90"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 7.293a1 1 0 011.414 0L10 11.586l4.293-4.293a1 1 0 111.414 1.414l-5 5a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <p className="w-fit my-3 text-gray-600 text-lg text-center font-semibold px-3 py-1">
          {pagination?.page} of{" "}
          <button
            onClick={() => {
              handlePush(`/blog/page/${pagination?.pages}`);
            }}
          >
            {pagination?.pages}
          </button>
        </p>
        <button
          aria-label="Next"
          onClick={() => {
            handlePush(
              pagination?.next
                ? `/blog/page/${pagination?.next}`
                : `/blog/page/${pagination?.page}/#`
            );
          }}
          disabled={!pagination?.next}
          className={`${
            pagination?.next
              ? "hover:bg-[#a97dff] hover:text-white"
              : "opacity-50 cursor-not-allowed"
          } w-fit my-3 flex justify-center items-center text-lg text-center font-semibold px-1 py-1 border-2 border-[#915bff] rounded-lg text-[#915bff] transition-colors`}
        >
          <span></span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 -rotate-90"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 7.293a1 1 0 011.414 0L10 11.586l4.293-4.293a1 1 0 111.414 1.414l-5 5a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div> */}
      <div className="max-w-screen-mxl mx-auto flex justify-center items-center gap-1 my-10">
        <button
          aria-label="Previous"
          onClick={() => {
            handlePush(
              pagination?.prev
                ? `/blog/page/${pagination?.prev}`
                : `/blog/page/$${pagination?.page}/#`
            );
          }}
          disabled={!pagination?.prev}
          className={`${
            pagination?.prev
              ? "bg-accent-purple text-white"
              : " cursor-not-allowed opacity-50 bg-gray-200"
          } flex justify-center items-center text-lg  font-semibold px-6 h-12  rounded-full `}
        >
          Prev
        </button>
        <div className=" text-gray-600 text-lg flex justify-center items-center font-semibold px-4 h-12 gap-1">
          <span>{pagination?.page} of</span>
          <button
            onClick={() => {
              handlePush(`/blog/page/${pagination?.pages}`);
            }}
          >
            {pagination?.pages}
          </button>
        </div>
        <button
          aria-label="Next"
          onClick={() => {
            handlePush(
              pagination?.next
                ? `/blog/page/${pagination?.next}`
                : `/blog/page/${pagination?.page}/#`
            );
          }}
          disabled={!pagination?.next}
          className={`${
            pagination?.next
              ? "bg-accent-purple text-white"
              : " cursor-not-allowed opacity-50 bg-gray-200"
          }  flex justify-center items-center text-lg  font-semibold px-6 h-12  rounded-full`}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default Blogs;
